import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../../Common/Breadcrumbs';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import '../../../Common/ShimmerLoader.css'; // Ensure shimmer styles are loaded
import { getSellerById } from '../../../api/shop/sellers/sellerService';
import { getSellerLocations, deleteSellerLocation, updateSellerLocationStatus } from '../../../api/shop/sellers/sellerLocationService';
import { Seller, SellerLocation } from '../../../api/shop/types/sellerTypes';
import { StatusBadge, FulfillmentBadge } from '../../../Common/BadgeStyles';
import '../../../Common/BadgeStyles.css'; // Ensure badge styles are loaded
import SellerCard from './components/SellerCard';
import SellerFormModal from './components/SellerFormModal';
import LocationFormModal from './components/LocationFormModal';
import { DeleteModal } from '../../../Common/DeleteModal';
import { SellerSuccessModal } from './components/SellerSuccessModal';
import { updateSeller } from '../../../api/shop';
import { useSelector } from 'react-redux';
import { hasAnyPermission, type User } from '../../../utils/roleBasedAccess';
import './SellerDetail.css';

const SellerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  const userData: User = useMemo(
    () => ({
      ...(user || {}),
      subRole: (user && user.subRole) || role,
      permissions: permissions,
    }),
    [user, role, permissions]
  );
  const hasManagePermissions = hasAnyPermission(userData, [
    'shop.admin',
    'shop.seller.manage',
  ]);
  const [seller, setSeller] = useState<Seller | null>(null);
  const [locations, setLocations] = useState<SellerLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'All' | 'ACTIVE' | 'INACTIVE'>('All');
  const [locationPage, setLocationPage] = useState(1);
  const [locationTotalPages, setLocationTotalPages] = useState(1);
  const [locationTotalCount, setLocationTotalCount] = useState(0);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showLocationFormModal, setShowLocationFormModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<SellerLocation | null>(null);
  const [showLocationDeleteModal, setShowLocationDeleteModal] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<SellerLocation | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isUpdateOperation, setIsUpdateOperation] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState(false);

  const fetchSeller = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const sellerData = await getSellerById(id);
      setSeller(sellerData);
    } catch (err: any) {
      setError(err.message || 'Failed to load seller details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchLocations = useCallback(async () => {
    if (!id) return;
    try {
      setLocationsLoading(true);
      const response = await getSellerLocations({
        seller: id,
        status: statusFilter,
        pageNum: locationPage,
      });
      setLocations(response.paginatedResults || []);
      setLocationTotalPages(response.totalPages || 1);
      setLocationTotalCount(response.totalCount || 0);
    } catch (err: any) {
      // Error handled silently
    } finally {
      setLocationsLoading(false);
    }
  }, [id, statusFilter, locationPage]);

  useEffect(() => {
    fetchSeller();
  }, [fetchSeller]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const getSellerTypeLabel = (type: string) => {
    return type === 'PLATFORM_OWNED' ? 'Platform Owned' : 'Third Party';
  };

  const handleEditSeller = () => {
    if (!seller) return;
    if (!hasManagePermissions) return;
    setShowFormModal(true);
  };

  const handleFormSuccess = () => {
    // Prevent multiple calls
    if (showSuccessModal) return;
    
    const wasUpdate = seller !== null;
    setIsUpdateOperation(wasUpdate);
    setSuccessMessage(wasUpdate ? 'Seller has been updated successfully' : 'Seller has been created successfully');
    setShowFormModal(false);
    setShowSuccessModal(true);
    fetchSeller();
    fetchLocations();
  };

  const handleToggleStatus = async () => {
    if (!seller) return;
    if (!hasManagePermissions) return;
    try {
      const newStatus = seller.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await updateSeller(seller._id, {
        id: seller.id,
        type: seller.type,
        name: seller.name,
        supportedFulfillment: seller.supportedFulfillment,
        status: newStatus,
      });
      fetchSeller();
    } catch (error) {
      // Error handled silently
    }
  };

  const handleAddLocation = () => {
    if (!hasManagePermissions) return;
    setEditingLocation(null);
    setShowLocationFormModal(true);
  };

  const handleEditLocation = (location: SellerLocation) => {
    if (!hasManagePermissions) return;
    setEditingLocation(location);
    setShowLocationFormModal(true);
  };

  const handleLocationFormSuccess = () => {
    setShowLocationFormModal(false);
    setEditingLocation(null);
    fetchLocations(); // Refresh locations list
  };

  const handleDeleteLocation = (location: SellerLocation) => {
    if (!hasManagePermissions) return;
    setLocationToDelete(location);
    setShowLocationDeleteModal(true);
  };

  const handleLocationDeleteConfirm = async () => {
    if (!locationToDelete) return;
    if (!hasManagePermissions) return;

    try {
      setDeletingLocation(true);
      await deleteSellerLocation(locationToDelete._id);
      setShowLocationDeleteModal(false);
      setLocationToDelete(null);
      setSuccessMessage('Seller location has been deleted successfully');
      setShowSuccessModal(true);
      fetchLocations(); // Refresh locations list
    } catch (err: any) {
      // Error handled silently
    } finally {
      setDeletingLocation(false);
    }
  };

  const handleToggleLocationStatus = async (location: SellerLocation) => {
    if (!hasManagePermissions) return;
    const newStatus = location.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    
    // Optimistically update the local state immediately
    setLocations(prevLocations => 
      prevLocations.map(loc => 
        loc._id === location._id ? { ...loc, status: newStatus } : loc
      )
    );
    
    try {
      // Update on server
      await updateSellerLocationStatus(location._id, newStatus);
    } catch (error) {
      // Revert on error - refetch to sync with server state
      fetchLocations();
    }
  };

  if (loading) {
    return (
      <div className="page-content" style={{ marginTop: '20px' }}>
        <Container fluid>
          {/* Breadcrumbs Shimmer */}
          <div className="mb-3" style={{ padding: '0 15px' }}>
            <div className="placeholder" style={{ height: '24px', width: '300px', borderRadius: '4px' }}></div>
          </div>
          
          {/* Header Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '24px' }}>
            <div className="d-flex align-items-center" style={{ gap: '16px' }}>
              <div className="placeholder" style={{ width: '40px', height: '40px', borderRadius: '4px' }}></div>
              <div className="placeholder" style={{ height: '32px', width: '150px', borderRadius: '4px' }}></div>
            </div>
          </div>
          
          {/* Seller Details Card Shimmer */}
          <div style={{ padding: '0 15px', marginBottom: '32px' }}>
            <Card className="seller-detail-card">
              <Card.Body>
                <div className="placeholder" style={{ height: '200px', borderRadius: '8px' }}></div>
              </Card.Body>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content seller-detail-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={[
            { label: 'Shop Services', onClick: () => navigate('/shop-services/sellers') },
            { label: 'Sellers', onClick: () => navigate('/shop-services/sellers') },
            { label: seller?.name || 'Seller Details', active: true }
          ]} />
        </div>

        {/* Page Header with Back Button */}
        <div style={{ padding: '0 15px', marginBottom: '24px' }}>
          <div className="d-flex align-items-center" style={{ gap: '16px' }}>
            <button 
              className="back-button"
              onClick={() => navigate('/shop-services/sellers')}
            >
              <i className="ri-arrow-left-line"></i>
            </button>
            <h1 className="page-title mb-0">Sellers</h1>
          </div>
        </div>

        {/* Seller Details Section */}
        <div style={{ padding: '0 15px', marginBottom: '32px' }}>
          <Card className="seller-detail-card">
            <Card.Body>
              {seller ? (
                <>
                  {/* Header with Title and Buttons */}
                  <div className="seller-detail-header">
                    <h3 className="seller-detail-title">Seller Details</h3>
                    {hasManagePermissions && (
                      <div className="seller-detail-actions">
                        <Button
                          variant="primary"
                          className="seller-detail-action-btn"
                          onClick={handleEditSeller}
                        >
                          Edit Seller
                        </Button>
                        <Button
                          variant="outline-warning"
                          className="seller-detail-action-btn"
                          onClick={handleToggleStatus}
                        >
                          {seller.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  {/* Divider */}
                  <hr className="seller-detail-divider" />
                  
                  {/* Seller Information - Horizontal Layout */}
                  <div className="seller-detail-info">
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">Seller Name:</span>
                      <span className="seller-detail-value">{seller.name}</span>
                    </div>
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">ID:</span>
                      <span className="seller-detail-value">{seller.id}</span>
                    </div>
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">Created at:</span>
                      <span className="seller-detail-value">{formatDate(seller.createdAt || seller.createdDate)}</span>
                    </div>
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">Seller Status:</span>
                      <div className="seller-detail-badge-wrapper">
                        <StatusBadge status={seller.status as 'ACTIVE' | 'INACTIVE'} />
                      </div>
                    </div>
                    <div className="seller-detail-meta-item">
                      <span className="seller-detail-label">Fulfillment Capabilities:</span>
                      <div className="seller-detail-fulfillment-badges">
                        {(() => {
                          const sortedCapabilities = [...(seller.supportedFulfillment || [])].sort((a, b) => {
                            if (a === 'PICKUP') return -1;
                            if (b === 'PICKUP') return 1;
                            if (a === 'DELIVERY') return -1;
                            if (b === 'DELIVERY') return 1;
                            return 0;
                          });
                          return sortedCapabilities.map((capability) => (
                            <FulfillmentBadge 
                              key={capability} 
                              capability={capability as 'PICKUP' | 'DELIVERY'} 
                            />
                          ));
                        })()}
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="seller-detail-no-data">
                  <div className="seller-detail-no-data-icon">
                    <i className="ri-information-line"></i>
                  </div>
                  <h4 className="seller-detail-no-data-title">Data Not Available</h4>
                  <p className="seller-detail-no-data-message">
                    {error || 'Seller details could not be loaded. Please try again later.'}
                  </p>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>

        {/* Seller Locations Section */}
        <div style={{ padding: '0 15px' }}>
          <Card className="seller-locations-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="seller-locations-title mb-0">Seller Locations</h4>
                <div className="d-flex align-items-center gap-3">
                  <div style={{ width: '150px' }}>
                    <Select
                      classNamePrefix="react-select"
                      value={{
                        value: statusFilter,
                        label: statusFilter === 'All' ? 'All' : statusFilter === 'ACTIVE' ? 'Active' : 'Inactive'
                      }}
                      onChange={(selectedOption) => {
                        if (selectedOption) {
                          setStatusFilter(selectedOption.value as 'All' | 'ACTIVE' | 'INACTIVE');
                          setLocationPage(1); // Reset to first page when filter changes
                        }
                      }}
                      options={[
                        { value: 'All', label: 'All' },
                        { value: 'ACTIVE', label: 'Active' },
                        { value: 'INACTIVE', label: 'Inactive' }
                      ]}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
                      }}
                    />
                  </div>
                  {hasManagePermissions && (
                    <Button variant="primary" onClick={handleAddLocation}>
                      Add Seller Location
                    </Button>
                  )}
                </div>
              </div>

              {locationsLoading ? (
                <Row className="seller-locations-grid">
                  {[...Array(6)].map((_, index) => (
                    <Col key={index} md={6} lg={4} className="mb-4">
                      <div className="card" style={{ padding: '20px', minHeight: '200px' }}>
                        <div className="placeholder" style={{ height: '180px', borderRadius: '8px' }}></div>
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : locations.length === 0 ? (
                <div className="text-center py-5">
                  <p className="text-muted">No locations found</p>
                </div>
              ) : (
                <>
                  <Row className="seller-locations-grid">
                    {locations.map((location) => (
                      <Col key={location._id} md={6} lg={4} className="mb-4">
                        <SellerCard
                          location={location}
                          onEdit={hasManagePermissions ? () => handleEditLocation(location) : undefined}
                          onDelete={hasManagePermissions ? () => handleDeleteLocation(location) : undefined}
                          onToggleStatus={hasManagePermissions ? () => handleToggleLocationStatus(location) : undefined}
                        />
                      </Col>
                    ))}
                  </Row>
                  
                  {/* Pagination for Locations */}
                  {locationTotalPages > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4">
                      <div style={{ width: '150px' }}>
                        <Select
                          classNamePrefix="react-select"
                          value={{
                            value: locationPage,
                            label: `Page ${locationPage}`
                          }}
                          onChange={(selectedOption: any) => {
                            if (selectedOption) {
                              setLocationPage(selectedOption.value);
                            }
                          }}
                          options={Array.from({ length: locationTotalPages }, (_, i) => ({
                            value: i + 1,
                            label: `Page ${i + 1}`
                          }))}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 })
                          }}
                        />
                      </div>
                      <div className="text-muted">
                        Showing {((locationPage - 1) * 10) + 1} - {Math.min(locationPage * 10, locationTotalCount)} of {locationTotalCount} locations
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </div>
      </Container>

      {/* Form Modal */}
      {showFormModal && seller && (
        <SellerFormModal
          show={showFormModal}
          onHide={() => setShowFormModal(false)}
          seller={seller}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Modal for Location */}
      <DeleteModal
        show={showLocationDeleteModal}
        handleClose={() => {
          setShowLocationDeleteModal(false);
          setLocationToDelete(null);
        }}
        deleteModalFunction={handleLocationDeleteConfirm}
        title="Delete Seller Location"
        message={`Are you sure you want to delete location "${locationToDelete?.locationCode || locationToDelete?.name || 'this location'}"? This action cannot be undone.`}
        isLoading={deletingLocation}
      />

      {/* Success Modal */}
      <SellerSuccessModal
        show={showSuccessModal}
        handleClose={() => {
          setShowSuccessModal(false);
          setSuccessMessage('');
          setIsUpdateOperation(false);
        }}
        title={isUpdateOperation ? 'Seller Updated Successfully' : 'Seller Created Successfully'}
        message={successMessage}
      />

      {/* Location Form Modal */}
      {seller && (
        <LocationFormModal
          show={showLocationFormModal}
          onHide={() => {
            setShowLocationFormModal(false);
            setEditingLocation(null);
          }}
          sellerId={seller._id}
          sellerFulfillmentCapabilities={seller.supportedFulfillment || []}
          location={editingLocation}
          onSuccess={handleLocationFormSuccess}
        />
      )}
    </div>
  );
};

export default SellerDetailPage;
